import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, ClockIcon, BabyIcon, HeartHandshakeIcon, PhoneIcon, MailIcon, CheckCircle2Icon, ShieldCheckIcon, LanguagesIcon, AwardIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { Link } from 'react-router-dom';
export function BookOnlinePage() {
  const {
    t
  } = useLanguage();
  const [selectedService, setSelectedService] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    id: 'pregnancy',
    title: 'Online Pregnancy Consultation',
    description: 'Expert guidance, birth preparation, and emotional support throughout your pregnancy journey.',
    duration: '60 min',
    icon: <HeartHandshakeIcon size={24} data-id="element-229" />
  }, {
    id: 'postnatal',
    title: 'Online Postnatal Support',
    description: 'Comprehensive care for your recovery, newborn care guidance, and emotional wellbeing.',
    duration: '45 min',
    icon: <BabyIcon size={24} data-id="element-230" />
  }, {
    id: 'breastfeeding',
    title: 'Online Breastfeeding Support',
    description: 'Personalized lactation advice, troubleshooting, and support for your feeding journey.',
    duration: '45 min',
    icon: <HeartHandshakeIcon size={24} data-id="element-231" />
  }];
  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    document.getElementById('booking-form')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="pt-24 pb-20 bg-white min-h-screen" data-id="element-232">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-teal-50/50" data-id="element-233">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-234">
          <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-teal-100/40 blur-3xl" data-id="element-235" />
          <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-teal-50/60 blur-3xl" data-id="element-236" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-237">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-238">
            <h1 className="text-4xl md:text-5xl font-serif font-medium text-teal-900 mb-6" data-id="element-239">
              Book Your Online Consultation
            </h1>
            <p className="text-xl text-slate-600 mb-4" data-id="element-240">
              Expert midwifery support from the comfort of your home
            </p>
            <p className="text-sm font-medium text-teal-600 tracking-wide uppercase mb-10" data-id="element-241">
              → Available across New Zealand & internationally
            </p>

            {!isSubmitted && <button onClick={() => document.getElementById('booking-form')?.scrollIntoView({
            behavior: 'smooth'
          })} className="inline-flex justify-center items-center bg-teal-600 hover:bg-teal-700 text-white px-8 py-3.5 rounded-full text-base font-medium transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5" data-id="element-242">
                Book Now
              </button>}
          </motion.div>
        </div>
      </section>

      {isSubmitted /* Confirmation State */ ? <section className="py-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-243">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-teal-50/50 rounded-3xl p-12 border border-teal-100" data-id="element-244">
            <div className="w-20 h-20 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-245">
              <CheckCircle2Icon size={40} data-id="element-246" />
            </div>
            <h2 className="text-3xl font-serif font-medium text-teal-900 mb-4" data-id="element-247">
              Your request has been received
            </h2>
            <p className="text-lg text-slate-600 mb-8" data-id="element-248">
              Thank you for reaching out. I will contact you shortly to confirm
              your appointment details and provide a link for our online
              consultation.
            </p>
            <Link to="/" className="inline-flex justify-center items-center bg-teal-600 hover:bg-teal-700 text-white px-8 py-3.5 rounded-full text-base font-medium transition-all shadow-sm" data-id="element-249">
              Return to Homepage
            </Link>
          </motion.div>
        </section> : <>
          {/* Clarity Notice */}
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-250">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white rounded-2xl shadow-lg border border-teal-100 p-6 md:p-8 text-center" data-id="element-251">
              <p className="text-lg font-medium text-slate-800 mb-2" data-id="element-252">
                Online consultations can be booked instantly below.
              </p>
              <p className="text-slate-600" data-id="element-253">
                For in-person services in Auckland, please contact me directly
                via email or text.
              </p>
            </motion.div>
          </section>

          {/* Services Selection */}
          <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-254">
            <div className="text-center mb-12" data-id="element-255">
              <h2 className="text-3xl font-serif font-medium text-teal-900 mb-4" data-id="element-256">
                Select a Service
              </h2>
              <p className="text-slate-600" data-id="element-257">
                Choose the online consultation that best fits your needs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-258">
              {services.map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`bg-white rounded-2xl p-8 border-2 transition-all cursor-pointer hover:shadow-md ${selectedService === service.id ? 'border-teal-500 shadow-md' : 'border-teal-50 hover:border-teal-200'}`} onClick={() => handleServiceSelect(service.id)} data-id="element-259">
                  <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center mb-6" data-id="element-260">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-medium text-slate-800 mb-3" data-id="element-261">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 min-h-[80px]" data-id="element-262">
                    {service.description}
                  </p>

                  <div className="flex items-center justify-between mt-auto" data-id="element-263">
                    <div className="flex items-center text-sm font-medium text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg" data-id="element-264">
                      <ClockIcon size={16} className="mr-2" data-id="element-265" />
                      {service.duration}
                    </div>
                    <button className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${selectedService === service.id ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`} data-id="element-266">
                      {selectedService === service.id ? 'Selected' : 'Select'}
                    </button>
                  </div>
                </motion.div>)}
            </div>
          </section>

          {/* Booking Form */}
          <section id="booking-form" className="py-12 bg-slate-50/50" data-id="element-267">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-268">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-12" data-id="element-269">
                <div className="text-center mb-10" data-id="element-270">
                  <h2 className="text-3xl font-serif font-medium text-teal-900 mb-4" data-id="element-271">
                    Request Appointment
                  </h2>
                  <p className="text-slate-600" data-id="element-272">
                    Fill out your details and I will confirm your booking
                    shortly.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-273">
                  <div className="grid md:grid-cols-2 gap-6" data-id="element-274">
                    <div data-id="element-275">
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-276">
                        Full Name
                      </label>
                      <input type="text" id="name" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all" placeholder="Jane Doe" data-id="element-277" />
                    </div>
                    <div data-id="element-278">
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-279">
                        Email Address
                      </label>
                      <input type="email" id="email" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all" placeholder="jane@example.com" data-id="element-280" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6" data-id="element-281">
                    <div data-id="element-282">
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-283">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all" placeholder="+64 21 000 0000" data-id="element-284" />
                    </div>
                    <div data-id="element-285">
                      <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-286">
                        Service
                      </label>
                      <select id="service" required value={selectedService} onChange={e => setSelectedService(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all bg-white" data-id="element-287">
                        <option value="" disabled data-id="element-288">
                          Select a service
                        </option>
                        {services.map(s => <option key={s.id} value={s.id} data-id="element-289">
                            {s.title}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6" data-id="element-290">
                    <div data-id="element-291">
                      <label htmlFor="date" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-292">
                        Preferred Date
                      </label>
                      <div className="relative" data-id="element-293">
                        <input type="date" id="date" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all" data-id="element-294" />
                      </div>
                    </div>
                    <div data-id="element-295">
                      <label htmlFor="time" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-296">
                        Preferred Time
                      </label>
                      <div className="relative" data-id="element-297">
                        <input type="time" id="time" required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all" data-id="element-298" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-299">
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-300">
                      Message (Optional)
                    </label>
                    <textarea id="message" rows={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all resize-none" placeholder="Any specific topics you'd like to discuss?" data-id="element-301"></textarea>
                  </div>

                  <button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white px-6 py-4 rounded-xl font-medium text-lg transition-colors shadow-sm flex justify-center items-center" data-id="element-302">
                    Submit Booking Request
                    <ArrowRightIcon size={20} className="ml-2" data-id="element-303" />
                  </button>
                </form>
              </motion.div>
            </div>
          </section>
        </>}

      {/* Trust Badges */}
      <section className="py-16 border-t border-slate-100" data-id="element-304">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-305">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-306">
            <div className="flex flex-col items-center text-center" data-id="element-307">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-308">
                <ShieldCheckIcon size={24} data-id="element-309" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-310">
                Certified Midwife
              </h4>
              <p className="text-sm text-slate-500" data-id="element-311">16+ years experience</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-312">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-313">
                <LanguagesIcon size={24} data-id="element-314" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-315">Bilingual</h4>
              <p className="text-sm text-slate-500" data-id="element-316">English & French</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-317">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-318">
                <HeartHandshakeIcon size={24} data-id="element-319" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-320">
                Trauma-Informed
              </h4>
              <p className="text-sm text-slate-500" data-id="element-321">Compassionate care</p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-322">
              <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mb-4" data-id="element-323">
                <AwardIcon size={24} data-id="element-324" />
              </div>
              <h4 className="font-medium text-slate-800 mb-1" data-id="element-325">BBC 100 Women</h4>
              <p className="text-sm text-slate-500" data-id="element-326">Honoree</p>
            </div>
          </div>
        </div>
      </section>

      {/* In-Person Contact Section */}
      <section className="py-16 bg-teal-900 text-white" data-id="element-327">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-328">
          <h2 className="text-2xl md:text-3xl font-serif font-medium mb-6" data-id="element-329">
            In-Person Consultations (Auckland Only)
          </h2>
          <p className="text-teal-100 mb-10 max-w-2xl mx-auto text-lg" data-id="element-330">
            For home visits or in-person support in the Auckland area, please
            get in touch directly to discuss availability.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-10" data-id="element-331">
            <div className="flex items-center space-x-3 bg-teal-800/50 px-6 py-4 rounded-2xl" data-id="element-332">
              <MailIcon className="text-teal-300" size={24} data-id="element-333" />
              <div className="text-left" data-id="element-334">
                <p className="text-xs text-teal-200 uppercase tracking-wider" data-id="element-335">
                  Email
                </p>
                <a href="mailto:thehopecircle.ase@gmail.com" className="text-white hover:text-teal-200 transition-colors" data-id="element-336">
                  thehopecircle.ase@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-3 bg-teal-800/50 px-6 py-4 rounded-2xl" data-id="element-337">
              <PhoneIcon className="text-teal-300" size={24} data-id="element-338" />
              <div className="text-left" data-id="element-339">
                <p className="text-xs text-teal-200 uppercase tracking-wider" data-id="element-340">
                  Phone
                </p>
                <p className="text-white" data-id="element-341">+64(0)273737738</p>
              </div>
            </div>
          </div>

          <a href="mailto:thehopecircle.ase@gmail.com" className="inline-flex justify-center items-center bg-white text-teal-900 hover:bg-teal-50 px-8 py-3.5 rounded-full text-base font-medium transition-colors shadow-sm" data-id="element-342">
            Contact Me
          </a>
        </div>
      </section>
    </div>;
}